import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as wordUtils from '../utils/wordUtils';

export const useWordDictionary = () => {
  const [dictionary, setDictionary] = useState({
    all: [],
    byFirstLetter: {},
    byEffectiveLetter: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dictionary from file
  useEffect(() => {
    const loadDictionary = async () => {
      try {
        setIsLoading(true);
        // Fix: Update the path to match your actual file name (with hyphens instead of underscores)
        const response = await axios.get('/data/uyghur-words.txt');
        const processedDictionary = wordUtils.processDictionaryText(response.data);
        setDictionary(processedDictionary);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading dictionary:', err);
        setError(err);
        setIsLoading(false);
        
        // Try loading the sample dictionary as a fallback
        try {
          const sampleResponse = await axios.get('/data/sample_uyghur_words.txt');
          const processedSample = wordUtils.processDictionaryText(sampleResponse.data);
          setDictionary(processedSample);
          console.log('Loaded sample dictionary as fallback');
        } catch (sampleErr) {
          console.error('Failed to load sample dictionary:', sampleErr);
        }
      }
    };

    loadDictionary();
  }, []);

  // Get all words starting with a specific letter
  const getWordsStartingWith = useCallback((letter) => {
    if (!dictionary.byFirstLetter || !dictionary.byFirstLetter[letter]) {
      return [];
    }
    return dictionary.byFirstLetter[letter];
  }, [dictionary]);

  // Get words that can follow a specific letter (handling Uyghur orthography)
  const getWordsThatCanFollow = useCallback((letter) => {
    // Check if this is a vowel that requires hamza
    const vowelsRequiringHamza = ['ا', 'ە', 'و', 'ۆ', 'ۇ', 'ۈ', 'ى', 'ې'];
    
    if (vowelsRequiringHamza.includes(letter)) {
      // For vowels, we need words starting with hamza where the second letter is this vowel
      return dictionary.byEffectiveLetter[letter] || [];
    }
    
    // For regular letters, just get words starting with this letter
    return dictionary.byFirstLetter[letter] || [];
  }, [dictionary]);

  // Check if a word exists in the dictionary
  const isValidWord = useCallback((word) => {
    if (!dictionary.all) return false;
    return dictionary.all.includes(word);
  }, [dictionary]);

  // Get a random word from the dictionary
  const getRandomWord = useCallback(() => {
    if (!dictionary.all || dictionary.all.length === 0) {
      return ''; // Return empty string if dictionary is not loaded
    }
    return dictionary.all[Math.floor(Math.random() * dictionary.all.length)];
  }, [dictionary]);

  // Get a random word starting with a specific letter
  const getRandomWordStartingWith = useCallback((letter) => {
    const words = getWordsStartingWith(letter);
    if (words.length === 0) return '';
    return words[Math.floor(Math.random() * words.length)];
  }, [getWordsStartingWith]);

  return {
    isLoading,
    error,
    getWordsStartingWith,
    getWordsThatCanFollow,
    isValidWord,
    getRandomWord,
    getRandomWordStartingWith
  };
};

export default useWordDictionary;